import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "src/lib/utils"

const badgeVariants = cva(
  "inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-gray-100 text-gray-700 shadow hover:bg-gray-200",
        primary:
          "border-transparent bg-primary-500 text-white shadow hover:bg-primary-600",
        secondary:
          "border-transparent bg-secondary-500 text-white hover:bg-secondary-600",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground shadow hover:bg-destructive/80",
        outline: "border-primary-200 text-primary-700 hover:bg-primary-200",
        "outline-gradient": "bg-gradient-to-br from-primary-600/10 to-secondary-600/10 rounded-md text-primary border-primary-200 border",
        purple:
          "bg-primary-100 text-primary-700 hover:bg-primary-200",
        gradient:
          "bg-gradient-to-r from-primary-600/90 to-secondary-600/90 rounded-md text-primary-100 border-primary-200 border",
        gray:
          "bg-gradient-to-r from-gray-400 to-slate-600 rounded-md text-gray-100 border-gray-200 border",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

function Badge({
  className,
  variant,
  asChild = false,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : "span"

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  )
}

export { Badge, badgeVariants }
